import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Link } from "react-router-dom";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import ComparisonTable from "@/components/ComparisonTable";
import ProductCard from "@/components/ProductCard";
import EmailCapture from "@/components/EmailCapture";
import SEOHead from "@/components/SEOHead";
import TableOfContents from "@/components/TableOfContents";
import { ChevronRight, Zap, Battery, Sun } from "lucide-react";

const TOC = [
  { id: "top-picks", label: "Top Solar Panels for Solar Generators" },
  { id: "comparison", label: "Quick Comparison Table" },
  { id: "how-to-choose", label: "How to Choose the Right Panel" },
  { id: "compatibility", label: "Generator Compatibility Guide" },
  { id: "top-generators", label: "Top Solar Generators to Pair With" },
  { id: "faq", label: "Frequently Asked Questions" },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Best Solar Panels for Solar Generators (2026)",
  "description": "Find the best solar panels compatible with EcoFlow, Jackery, Bluetti, and Anker solar generators. Compare wattage, efficiency, and connector types.",
  "url": "https://highestefficiencysolarcell.com/best-solar-panels-for-solar-generators",
  "dateModified": "2026-06-01",
  "author": { "@type": "Organization", "name": "HighestEfficiencySolarCell.com" },
  "publisher": { "@type": "Organization", "name": "HighestEfficiencySolarCell.com" },
};

export default function BestSolarGeneratorPanels() {
  const { data: products = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: () => base44.entities.SolarProduct.list("sort_order", 50),
  });

  const generatorPanels = products.filter((p) =>
    p.best_for_category === "Best for Solar Generator" ||
    p.form_factor === "Portable" ||
    p.form_factor === "Foldable"
  );

  const displayProducts = generatorPanels.length > 0 ? generatorPanels : products.slice(0, 6);

  return (
    <>
      <SEOHead
        title="Best Solar Panels for Solar Generators 2026 | EcoFlow, Jackery, Bluetti Compatible"
        description="Find the best solar panels for solar generators including EcoFlow, Jackery, Bluetti, and Anker. Compare wattage, efficiency, connectors, and compatibility."
        canonical="https://highestefficiencysolarcell.com/best-solar-panels-for-solar-generators"
        schema={SCHEMA}
      />

      <div className="max-w-7xl mx-auto px-4 py-10 pb-20 lg:pb-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-accent">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-foreground">Best Solar Panels for Solar Generators</span>
        </nav>

        <div className="max-w-4xl">
          <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">
            Best Solar Panels for Solar Generators (2026)
          </h1>
          <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
            Pairing the right solar panel with your solar generator dramatically affects how fast you recharge and how much power you can use per day. This guide covers the best high-efficiency solar panels compatible with EcoFlow, Jackery, Bluetti, Anker SOLIX, and other popular solar generators — with focus on wattage, connector types, and real-world charging speed.
          </p>
          <AffiliateDisclosure />
          <TableOfContents items={TOC} />
        </div>

        {/* Top Picks */}
        <section id="top-picks" className="mb-16">
          <h2 className="font-heading font-bold text-3xl mb-3">Top Solar Panels for Solar Generators</h2>
          <p className="text-muted-foreground mb-8">
            These panels are chosen for high efficiency, wide compatibility, portability, and fast recharge rates with major solar generator brands.
          </p>
          {displayProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayProducts.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="text-center py-12 bg-secondary/30 rounded-xl">
              <p className="text-muted-foreground mb-4">Loading direct product recommendations...</p>
              <p className="text-xs text-muted-foreground">Direct Amazon product link needed before publishing fallback CTAs.</p>
            </div>
          )}
        </section>

        {/* Comparison Table */}
        <section id="comparison" className="mb-16 bg-secondary/30 rounded-2xl p-6 lg:p-8">
          <h2 className="font-heading font-bold text-3xl mb-3">Quick Comparison Table</h2>
          <p className="text-muted-foreground mb-6">Side-by-side specs for solar panels optimized for use with portable solar generators.</p>
          <ComparisonTable products={displayProducts} />
        </section>

        {/* How to Choose */}
        <section id="how-to-choose" className="mb-16 max-w-4xl">
          <h2 className="font-heading font-bold text-3xl mb-6">How to Choose a Solar Panel for Your Generator</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Zap,
                title: "Match Wattage to Input Limits",
                body: "Every solar generator has a maximum solar input wattage. Don't exceed it — check your generator's spec sheet. Running near (but not over) the limit gives the fastest recharge."
              },
              {
                icon: Battery,
                title: "Check Voltage Compatibility",
                body: "Panels must fall within the generator's acceptable voltage range (VOC). Connecting panels in series increases voltage — know your generator's max VOC before chaining multiple panels."
              },
              {
                icon: Sun,
                title: "Connector Type Matters",
                body: "Most generators use MC4, XT60, or proprietary connectors. Look for panels that match or include the right adapter. Mismatched connectors are the #1 beginner setup mistake."
              },
            ].map(({ icon: CardIcon, title, body }) => (
              <div key={title} className="bg-card border border-border rounded-xl p-5">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mb-3">
                  <CardIcon className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Compatibility Guide */}
        <section id="compatibility" className="mb-16 max-w-4xl">
          <h2 className="font-heading font-bold text-3xl mb-6">Generator Compatibility Guide</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-foreground text-background">
                  <th className="py-3 px-4 text-left text-xs font-mono uppercase tracking-wider">Generator</th>
                  <th className="py-3 px-4 text-left text-xs font-mono uppercase tracking-wider">Max Solar Input</th>
                  <th className="py-3 px-4 text-left text-xs font-mono uppercase tracking-wider">Max VOC</th>
                  <th className="py-3 px-4 text-left text-xs font-mono uppercase tracking-wider">Connector</th>
                  <th className="py-3 px-4 text-left text-xs font-mono uppercase tracking-wider">Best Panel Match</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { gen: "EcoFlow DELTA 2", input: "500W", voc: "60V", conn: "XT60 + MC4 adapter", panel: "200–400W portable" },
                  { gen: "EcoFlow DELTA Pro", input: "1600W", voc: "150V", conn: "XT60 + MC4 adapter", panel: "400W panels in series" },
                  { gen: "Jackery Explorer 1000 Pro", input: "400W", voc: "12–30V", conn: "Anderson + DC7909", panel: "SolarSaga 100W ×4" },
                  { gen: "Jackery Explorer 2000 Pro", input: "1400W", voc: "12–30V", conn: "Anderson", panel: "SolarSaga 200W ×6" },
                  { gen: "Bluetti AC200P", input: "700W", voc: "150V", conn: "MC4", panel: "200–350W rigid panels" },
                  { gen: "Anker SOLIX C1000", input: "600W", voc: "60V", conn: "MC4", panel: "200W panels ×3" },
                ].map((row) => (
                  <tr key={row.gen} className="border-b border-border hover:bg-secondary/40">
                    <td className="py-3 px-4 font-medium text-sm">{row.gen}</td>
                    <td className="py-3 px-4 text-sm">{row.input}</td>
                    <td className="py-3 px-4 text-sm font-mono">{row.voc}</td>
                    <td className="py-3 px-4 text-sm">{row.conn}</td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">{row.panel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">* Always verify specs with your generator's official documentation before purchasing.</p>
        </section>

        {/* Top Generators */}
        <section id="top-generators" className="mb-16 bg-secondary/30 rounded-2xl p-6 lg:p-8 max-w-4xl">
          <h2 className="font-heading font-bold text-3xl mb-3">Top Solar Generators to Pair With</h2>
          <p className="text-muted-foreground mb-6">Not sure which generator to get? These are the most popular models buyers pair with high-efficiency solar panels.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { name: "EcoFlow DELTA 2", desc: "Best overall mid-size generator. 1kWh capacity, fast AC charging, 500W solar input.", path: "/solar-guides/best-solar-panel-for-ecoflow-delta" },
              { name: "Jackery Explorer 2000 Pro", desc: "Large capacity for extended off-grid use. 2kWh+ with strong solar input.", path: "/solar-guides/best-solar-panels-for-jackery" },
              { name: "Bluetti AC200P", desc: "High-capacity with MC4 direct connection. Excellent for home backup.", path: "/solar-guides/best-solar-panels-for-bluetti" },
              { name: "Anker SOLIX C1000", desc: "Smart app-connected generator with strong solar input and fast charging.", path: "/solar-guides/portable-solar-panel-buying-guide" },
            ].map((gen) => (
              <div key={gen.name} className="bg-card border border-border rounded-xl p-4 flex flex-col gap-3">
                <div>
                  <h3 className="font-heading font-semibold">{gen.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{gen.desc}</p>
                </div>
                <Link to={gen.path} className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-secondary border border-border rounded-lg text-xs font-heading font-semibold hover:border-accent/50 hover:text-accent transition-colors">
                  Read compatibility guide
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-16 max-w-3xl">
          <h2 className="font-heading font-bold text-3xl mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: "Can I use any solar panel with my solar generator?",
                a: "Not always. Your generator has a maximum wattage input and voltage range (VOC). The panel you choose must stay within both limits. Always check the generator's spec sheet before buying."
              },
              {
                q: "How fast will a 200W panel charge a 1kWh generator?",
                a: "In ideal conditions (5 peak sun hours), a 200W panel produces about 1,000Wh per day — enough to fully charge a 1kWh generator. In partial sun or cloudy conditions, expect 50–70% of that."
              },
              {
                q: "Can I connect multiple solar panels to one generator?",
                a: "Yes, if your generator supports it. You can connect panels in parallel (same voltage, adds watts) or series (adds voltage, same watts). Check your generator's max input voltage before wiring in series."
              },
              {
                q: "What's the best portable solar panel for a solar generator?",
                a: "Foldable panels in the 100–400W range are most popular. Look for ones with the right connector for your generator, high cell efficiency (22%+), and a kickstand for easy angling."
              },
            ].map(({ q, a }) => (
              <div key={q} className="bg-card border border-border rounded-xl p-5">
                <h3 className="font-heading font-semibold mb-2">{q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Internal Links */}
        <div className="border-t border-border pt-8 max-w-4xl">
          <h3 className="font-heading font-semibold mb-4">Related Guides</h3>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Best Portable Solar Panels", path: "/best-portable-solar-panels" },
              { label: "Best High-Efficiency Solar Panels", path: "/best-high-efficiency-solar-panels" },
              { label: "Solar Panel Calculator", path: "/solar-panel-calculator" },
              { label: "Flexible Solar Panels Guide", path: "/flexible-solar-panels" },
              { label: "Solar Cell vs Panel Explained", path: "/solar-cell-vs-solar-panel" },
            ].map((link) => (
              <Link key={link.path} to={link.path} className="px-4 py-2 bg-secondary rounded-lg text-sm font-medium hover:bg-accent hover:text-foreground transition-colors">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <EmailCapture source="solar-generators-page" />
    </>
  );
}